import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { useComputed, useSnackbar } from "@homebound/beam";
import omit from "lodash/omit";
import { type PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo } from "react";
import {
  initializeMaterialLeadTimeConfigFields,
  maybeMaterialLeadTimeConfig,
  maybeOptionPricingConfig,
} from "src/routes/admin/utils";
import {
  type MaterialListing_DetailFragment,
  type MaterialListing_DetailVariantFragment,
  useSaveMaterialListingMutation,
} from "src/routes/libraries/material-catalog/components/materialListingDetail.generated";
import {
  type MaterialListingFormState,
  type MaterialVariantFormValue,
  getPriceEstimateText,
  materialListingFormConfig,
  saveMaterialListingVariantInput,
} from "src/routes/libraries/material-catalog/components/materialListingForm";
import { useMaterialListingPageQuery } from "src/routes/libraries/material-catalog/material-listing-page/MaterialListingPage.generated";
import { MaterialCatalogListingsDocument } from "src/routes/libraries/material-catalog/MaterialCatalog.generated";
import { noop } from "src/utils";
import { ObjectState, useFormState } from "src/utils/formState";

type MaterialListingContextProps = {
  listing: MaterialListing_DetailFragment | undefined;
  /**
   * The listing's variants in *display* order — the default first, then by featured image.
   *
   * Ordered here rather than in the thumbnail strip so the strip leads with the default even when
   * that variant is not the first form row.
   */
  variants: MaterialListing_DetailVariantFragment[];
  selectedVariant: MaterialListing_DetailVariantFragment | undefined;
  /**
   * The selected variant's row in `formState.variants` — i.e. the editable half of `selectedVariant`.
   *
   * Derived here rather than in each section, because every one of them needs it and the lookup has to be
   * a `useComputed` to stay reactive as rows are added.
   */
  selectedVariantForm: ObjectState<MaterialVariantFormValue> | undefined;
  setSelectedVariant: (id: string | undefined) => void;
  /**
   * True while the URL is in create mode (`?createNew=1`) and the user is filling an unsaved row.
   */
  isEditingPendingVariant: boolean;
  /** Appends an empty variant row and enters create mode; a second click re-selects the existing unsaved row. */
  addVariant: VoidFunction;
  /**
   * Clones the selected variant into a new unsaved row (no id/code/images) and enters create mode.
   * Replaces any existing pending row so only one unsaved variant exists at a time.
   */
  copyVariant: VoidFunction;
  /** Selects the unsaved row (re-enters create mode) without adding another row. */
  selectPendingVariant: VoidFunction;
  /** Drops the unsaved row and returns to a saved variant selection. */
  discardPendingVariant: VoidFunction;
  formState: MaterialListingFormState;
  loading: boolean;
  save: () => Promise<void>;
};

const MaterialListingContext = createContext<MaterialListingContextProps>({
  listing: undefined,
  variants: [],
  selectedVariant: undefined,
  selectedVariantForm: undefined,
  setSelectedVariant: () => {},
  isEditingPendingVariant: false,
  addVariant: () => {},
  copyVariant: () => {},
  selectPendingVariant: () => {},
  discardPendingVariant: () => {},
  loading: false,
  formState: undefined!,
  save: () => Promise.resolve(),
});
type MaterialListingProviderProps = PropsWithChildren<{
  listing: MaterialListing_DetailFragment | undefined;
  loading: boolean;
  /** Controlled by the caller so the page can source it from the URL (`?mv=`). */
  selectedVariantId: string | undefined;
  /** Controlled by the caller so the page can source it from the URL (`?createNew=1`). */
  createNew: boolean;
  onSelectVariant: (id: string | undefined) => void;
  onCreateNew: VoidFunction;
}>;

/**
 * Owns the listing form state and save.
 *
 * The form spans the *whole* listing (`variants` is a form list), so every variant's edits are held
 * at once and switching the selected variant never re-inits or drops changes.
 */
export function MaterialListingProvider(props: MaterialListingProviderProps) {
  const { children, listing, loading, selectedVariantId, createNew, onSelectVariant, onCreateNew } = props;
  const [saveMaterialListing, { loading: saving }] = useSaveMaterialListingMutation({ onError: noop });
  const { triggerNotice } = useSnackbar();

  const defaultVariantId = listing?.defaultVariant?.id;
  const variants = useMemo(
    () =>
      (listing?.materialVariants ?? []).sortBy((mv) => [
        mv.id === defaultVariantId ? 0 : 1,
        mv.featuredImage?.sortOrder ?? 0,
      ]),
    [listing?.materialVariants, defaultVariantId],
  );
  const isEditingPendingVariant = createNew;
  const selectedVariant = isEditingPendingVariant ? undefined : variants.find((mv) => mv.id === selectedVariantId);

  const formConfig = useMemo(() => materialListingFormConfig({ canEditItem: true, canEditType: true }), []);

  const formState = useFormState({
    config: formConfig,
    init: {
      input: listing,
      map: (listing) => ({
        id: listing?.id,
        name: listing?.name,
        description: listing?.description,
        type: listing?.type.code,
        itemId: listing?.item.id,
        brandId: listing?.brand?.id,
        designNote: listing?.designNote,
        defaultVariantId: listing?.defaultVariant?.id,
        variants: listing?.materialVariants.map((mv) => ({
          id: mv.id,
          code: mv.code,
          canEdit: mv.canEdit,
          codeOverride: mv.codeOverride,
          modelNumber: mv.modelNumber,
          manufacturerUrl: mv.manufacturerUrl,
          deletedAt: mv.deletedAt,
          designFeedback: mv.designFeedback,
          mavIds: mv.materialAttributeValues.map((mav) => mav.id),
          priceEstimate: getPriceEstimateText(mv, "None added"),
          images: mv.images.map(({ id, sortOrder, asset }) => ({
            id,
            asset: { id: asset?.id, fileName: asset?.fileName, contentType: asset?.contentType },
            attachmentUrl: asset?.attachmentUrl,
            downloadUrl: asset?.downloadUrl,
            sortOrder,
          })),
          tags: mv.tags.map((t) => t.id),
          components: mv.components.map(({ id, qualifier, component }) => ({
            id,
            qualifier,
            componentId: component.id,
            componentItemId: component.listing.item.id,
            componentValues: component.materialAttributeValues.map((mav) => mav.id),
          })),
        })),
        optionPricingConfig: listing.optionPricingConfig ?? {},
        materialLeadTimeConfig: { ...initializeMaterialLeadTimeConfigFields(listing.materialLeadTimeConfig) },
      }),
    },
    loading,
  });

  // Deep-link / refresh with `?createNew=1`: ensure an empty form row exists once the listing is loaded.
  // Skip while saving — cache re-init assigns the pending row an id mid-save, and create mode would
  // otherwise add a second blank row.
  useEffect(() => {
    if (!createNew || loading || saving || !listing) return;
    if (!formState.variants.rows.some((v) => !v.id.value)) {
      formState.variants.add({});
    }
  }, [createNew, loading, saving, listing, formState]);

  const selectedVariantForm = useComputed(() => {
    if (isEditingPendingVariant) {
      return formState.variants.rows.find((v) => !v.id.value);
    }
    return formState.variants.rows.find((v) => v.id.value === selectedVariantId);
  }, [formState, selectedVariantId, isEditingPendingVariant]);

  const setSelectedVariant = useCallback(
    (id: string | undefined) => {
      onSelectVariant(id);
    },
    [onSelectVariant],
  );

  const addVariant = useCallback(() => {
    if (!formState.variants.rows.some((v) => !v.id.value)) {
      formState.variants.add({});
    }
    onCreateNew();
  }, [formState, onCreateNew]);

  const copyVariant = useCallback(() => {
    const source = isEditingPendingVariant
      ? formState.variants.rows.find((v) => !v.id.value)
      : formState.variants.rows.find((v) => v.id.value === selectedVariantId);
    if (!source) return;
    // Snapshot before removing any pending row (source may be that row).
    const cloned = omit(source.value, "id", "code", "images", "canEdit", "priceEstimate");
    const pending = formState.variants.rows.find((v) => !v.id.value);
    if (pending) formState.variants.remove(pending.value);
    formState.variants.add(cloned);
    onCreateNew();
  }, [formState, isEditingPendingVariant, selectedVariantId, onCreateNew]);

  const selectPendingVariant = useCallback(() => onCreateNew(), [onCreateNew]);
  const discardPendingVariant = useCallback(() => {
    const pending = formState.variants.rows.find((v) => !v.id.value);
    if (pending) formState.variants.remove(pending.value);
    onSelectVariant(defaultVariantId ?? variants.first?.id);
  }, [formState, onSelectVariant, defaultVariantId, variants]);

  const save = useCallback(async () => {
    if (!formState.dirty) return;

    const priorIds = new Set(formState.variants.rows.map((v) => v.id.value).compact());
    const wasPending = isEditingPendingVariant && formState.variants.rows.some((v) => !v.id.value);
    const {
      variants: changedVariants,
      optionPricingConfig,
      materialLeadTimeConfig,
      ...others
    } = formState.changedValue;
    // List-level `changedValue` only signals dirtiness; build inputs from each row's sparse
    // `changedValue` plus full `value` (for exhaustive images/components).
    const variantInputs = changedVariants
      ? formState.variants.rows.map((row) => saveMaterialListingVariantInput(row.changedValue, row.value))
      : undefined;

    const { data, error } = await saveMaterialListing({
      errorPolicy: "all", // Return errors so this flow can replace the default error banner with a tailored message.
      variables: {
        input: {
          ...others,
          variants: variantInputs,
          ...maybeOptionPricingConfig({ ...formState.optionPricingConfig.value }),
          ...maybeMaterialLeadTimeConfig({ ...formState.materialLeadTimeConfig.value }),
        },
      },
      refetchQueries: [MaterialCatalogListingsDocument],
    });

    if (CombinedGraphQLErrors.is(error) && error.errors.nonEmpty) {
      const mvUniquenessError = error.errors.some(
        (e) => e.message.includes("have the same attributes") || e.message.includes("material_variants_code_key"),
      );

      triggerNotice({
        message: mvUniquenessError
          ? "This material conflicts with an existing material. Please add or alter it's attributes to make it unique"
          : error.errors.map((e) => e.message).join(", "),
        icon: "error",
      });

      return;
    }

    const savedMaterial = data?.saveMaterialListing;

    if (savedMaterial) {
      // Apply the mutation result so server-assigned `mac:` ids land on the form before the next
      // edit. Otherwise a dirty-list refresh can leave the just-saved row without its id, and the
      // following exhaustive components save drops it.
      formState.update(savedMaterial);
      triggerNotice({ message: `Material saved successfully`, icon: "success" });
      if (wasPending) {
        // Drop any blank row the create-mode effect may have added mid-save.
        formState.variants.rows.filter((v) => !v.id.value).forEach((v) => formState.variants.remove(v.value));
        // Select the newly created variant (fallback: last/appended row).
        const addedId = savedMaterial.materialVariants.find((v) => !priorIds.has(v.id))?.id;
        setSelectedVariant(addedId ?? savedMaterial.materialVariants.last?.id);
      }
    }
  }, [formState, saveMaterialListing, triggerNotice, isEditingPendingVariant, setSelectedVariant]);

  return (
    <MaterialListingContext.Provider
      value={{
        listing,
        variants,
        selectedVariant,
        selectedVariantForm,
        setSelectedVariant,
        isEditingPendingVariant,
        addVariant,
        copyVariant,
        selectPendingVariant,
        discardPendingVariant,
        formState,
        loading: loading || saving,
        save,
      }}
    >
      {children}
    </MaterialListingContext.Provider>
  );
}

type MaterialListingByListingIdProviderProps = PropsWithChildren<{
  listingId: string;
  selectedVariantId: string | undefined;
  createNew: boolean;
  onSelectVariant: (id: string | undefined) => void;
  onCreateNew: VoidFunction;
  /** Called once listing data is available so the page can canonicalize the URL. */
  onListingLoaded?: (listing: MaterialListing_DetailFragment) => void;
}>;

/**
 * Detail-page entry point: loads the listing directly and takes its selection from the caller
 * (the URL). Because `listingId` is the only variable, switching variants leaves the query — and so
 * the form — untouched.
 */
export function MaterialListingByListingIdProvider(props: MaterialListingByListingIdProviderProps) {
  const { children, listingId, selectedVariantId, createNew, onSelectVariant, onCreateNew, onListingLoaded } = props;
  const { data, loading } = useMaterialListingPageQuery({ variables: { listingId } });
  const listing = data?.materialListings.entities.first;

  useEffect(() => {
    if (listing) onListingLoaded?.(listing);
  }, [listing, onListingLoaded]);

  return (
    <MaterialListingProvider
      listing={listing}
      loading={loading}
      selectedVariantId={selectedVariantId}
      createNew={createNew}
      onSelectVariant={onSelectVariant}
      onCreateNew={onCreateNew}
    >
      {children}
    </MaterialListingProvider>
  );
}

export function useMaterialListingContext() {
  return useContext(MaterialListingContext);
}
